import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Select } from "antd";
import {
  NO_COMMISSION_CODE,
  NO_COMMISSION_KEY,
  NO_COMMISSION_OPTION,
  buildCommissionOptions,
  findCommissionOption,
  getSourceTypeForRule,
  isNoCommission,
  optionKey,
} from "../constants/commissionOptions";
import { getConfig } from "services/config.service";
import { updateSaleCommission } from "services/salesService";
import {
  notifyCommissionUpdated,
  subscribeCommissionUpdated,
} from "utils/commissionEvents";

const SALE_TEAM_CODE = "sale_team";
const SELECT_WIDTH = 185;

function getDisplayLabel(code, percent, options) {
  if (code === NO_COMMISSION_CODE || isNoCommission(code, percent)) {
    return NO_COMMISSION_OPTION.label;
  }
  const opt = findCommissionOption(code, percent, options);
  if (!opt) {
    if (Number.isFinite(Number(percent))) return `${code} (${percent}%)`;
    return undefined;
  }
  if (opt.percents || code === SALE_TEAM_CODE) return `Sale Team (${opt.percent}%)`;
  return opt.label;
}

function MenuItem({ active, children, onClick, onMouseEnter, trailing }) {
  return (
    <div
      role="option"
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      className={`relative flex items-center justify-between gap-2 px-2.5 py-1.5 text-sm cursor-pointer whitespace-nowrap transition-colors ${
        active ? "bg-gray-100 text-gray-900" : "text-gray-800 hover:bg-gray-100"
      }`}
    >
      <span>{children}</span>
      {trailing}
    </div>
  );
}

export default function SaleCommissionAssignment({
  sale,
  api,
  onUpdated,
  onToast,
  onSavingChange,
}) {
  const [value, setValue] = useState(undefined);
  const [isSaving, setIsSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [hoverSaleTeam, setHoverSaleTeam] = useState(false);
  const [submenuPos, setSubmenuPos] = useState({ top: 0, left: 0 });
  const [options, setOptions] = useState(() => buildCommissionOptions());
  const closeTimer = useRef(null);
  const saleTeamRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const loadOptions = () => {
      getConfig(api)
        .then((config) => {
          if (!cancelled) setOptions(buildCommissionOptions(config));
        })
        .catch(() => {});
    };
    loadOptions();
    const unsubscribe = subscribeCommissionUpdated((detail) => {
      if (detail?.type === "config") loadOptions();
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [api]);

  useEffect(() => {
    if (isNoCommission(sale.commissionRuleCode, sale.commissionPercent)) {
      setValue(NO_COMMISSION_KEY);
      return;
    }
    if (!sale.commissionRuleCode || !Number.isFinite(Number(sale.commissionPercent))) {
      setValue(undefined);
      return;
    }
    setValue(optionKey(sale.commissionRuleCode, sale.commissionPercent));
  }, [sale._id, sale.commissionRuleCode, sale.commissionPercent]);

  useEffect(() => () => clearCloseTimer(), []);

  const displayLabel = useMemo(() => {
    if (!value) return undefined;
    if (value === NO_COMMISSION_KEY) return NO_COMMISSION_OPTION.label;
    const [code, percentStr] = String(value).split(":");
    return getDisplayLabel(code, Number(percentStr), options);
  }, [value, options]);

  const saleTeamPercents = useMemo(() => {
    const saleTeam = options.find((opt) => opt.code === SALE_TEAM_CODE);
    return saleTeam?.percents ?? [];
  }, [options]);

  const clearCloseTimer = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const scheduleCloseSubmenu = () => {
    clearCloseTimer();
    closeTimer.current = setTimeout(() => setHoverSaleTeam(false), 180);
  };

  const openSaleTeamSubmenu = () => {
    clearCloseTimer();
    const el = saleTeamRef.current;
    if (el) {
      const rect = el.getBoundingClientRect();
      setSubmenuPos({
        top: rect.top,
        left: rect.right + 4,
      });
    }
    setHoverSaleTeam(true);
  };

  const assign = async ({ code, percent }) => {
    const prevValue = value;
    const nextValue =
      code === NO_COMMISSION_CODE ? NO_COMMISSION_KEY : optionKey(code, percent);
    setValue(nextValue);
    setOpen(false);
    setHoverSaleTeam(false);
    setIsSaving(true);
    onSavingChange?.(sale._id, true);

    try {
      const payload =
        code === NO_COMMISSION_CODE
          ? { commissionRuleCode: NO_COMMISSION_CODE }
          : { commissionRuleCode: code, commissionPercent: percent };

      const updated = await updateSaleCommission(api, sale._id, payload);

      if (
        code === NO_COMMISSION_CODE ||
        isNoCommission(updated.commissionRuleCode, updated.commissionPercent)
      ) {
        setValue(NO_COMMISSION_KEY);
        onUpdated?.(sale._id, {
          commissionRuleCode: updated.commissionRuleCode ?? null,
          commissionPercent: updated.commissionPercent ?? null,
          commissionAmount: updated.commissionAmount ?? null,
          commissionSourceType: updated.commissionSourceType ?? null,
        });
        notifyCommissionUpdated({
          saleId: sale._id,
          commissionRuleCode: NO_COMMISSION_CODE,
        });
        onToast?.({ message: "Commission cleared successfully.", type: "success" });
        return;
      }

      const nextPercent = Number(updated.commissionPercent ?? percent);
      const amount =
        updated.commissionAmount != null
          ? Number(updated.commissionAmount)
          : (Number(sale.totalAmount || 0) * nextPercent) / 100;
      onUpdated?.(sale._id, {
        commissionRuleCode: updated.commissionRuleCode ?? code,
        commissionPercent: nextPercent,
        commissionAmount: amount,
        commissionSourceType:
          updated.commissionSourceType ?? getSourceTypeForRule(code),
      });
      notifyCommissionUpdated({
        saleId: sale._id,
        commissionRuleCode: updated.commissionRuleCode ?? code,
      });
      onToast?.({ message: "Commission assigned successfully.", type: "success" });
    } catch (err) {
      setValue(prevValue);
      onToast?.({
        message: err?.response?.data?.message || "Failed to assign commission.",
        type: "error",
      });
    } finally {
      setIsSaving(false);
      onSavingChange?.(sale._id, false);
    }
  };

  const submenu =
    hoverSaleTeam &&
    createPortal(
      <div
        className="fixed z-[1200] bg-white rounded-lg border border-gray-200 shadow-md py-1 w-max"
        style={{ top: submenuPos.top, left: submenuPos.left }}
        onMouseEnter={clearCloseTimer}
        onMouseLeave={scheduleCloseSubmenu}
      >
        {saleTeamPercents.map((p) => {
          const key = optionKey(SALE_TEAM_CODE, p);
          return (
            <MenuItem
              key={key}
              active={value === key}
              onClick={() => assign({ code: SALE_TEAM_CODE, percent: p })}
            >
              {p}%
            </MenuItem>
          );
        })}
      </div>,
      document.body
    );

  const dropdownRender = () => (
    <div className="relative py-1" style={{ width: SELECT_WIDTH }}>
      <MenuItem
        active={value === NO_COMMISSION_KEY}
        onMouseEnter={() => setHoverSaleTeam(false)}
        onClick={() => assign({ code: NO_COMMISSION_CODE })}
      >
        {NO_COMMISSION_OPTION.label}
      </MenuItem>
      {options.map((opt) => {
        if (opt.percents) {
          const isActive =
            value != null && String(value).startsWith(`${SALE_TEAM_CODE}:`);
          return (
            <div
              key={opt.code}
              ref={saleTeamRef}
              onMouseEnter={openSaleTeamSubmenu}
              onMouseLeave={scheduleCloseSubmenu}
            >
              <MenuItem
                active={isActive || hoverSaleTeam}
                trailing={<span className="text-gray-400 text-xs">›</span>}
              >
                {opt.label}
              </MenuItem>
            </div>
          );
        }

        const key = optionKey(opt.code, opt.percent);
        return (
          <MenuItem
            key={key}
            active={value === key}
            onMouseEnter={() => setHoverSaleTeam(false)}
            onClick={() => assign({ code: opt.code, percent: opt.percent })}
          >
            {opt.label}
          </MenuItem>
        );
      })}
      {submenu}
    </div>
  );

  return (
    <div
      className="flex items-center gap-1.5 w-max"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <Select
        value={value}
        open={open}
        onDropdownVisibleChange={(next) => {
          setOpen(next);
          if (!next) {
            clearCloseTimer();
            setHoverSaleTeam(false);
          }
        }}
        options={value ? [{ value, label: displayLabel }] : []}
        placeholder="Select rule"
        disabled={isSaving}
        className="commission-rule-select"
        style={{ width: SELECT_WIDTH }}
        allowClear={false}
        dropdownRender={dropdownRender}
        popupClassName="commission-rule-select-dropdown"
        getPopupContainer={() => document.body}
        popupMatchSelectWidth
        showSearch={false}
      />
      {isSaving && (
        <span className="text-xs text-gray-500 whitespace-nowrap">Loading…</span>
      )}
    </div>
  );
}

export function formatCommissionDisplay(sale, isSaving = false) {
  if (isSaving) return "Loading…";

  if (isNoCommission(sale.commissionRuleCode, sale.commissionPercent)) {
    return "-";
  }

  const percent = Number(sale.commissionPercent);
  const hasPercent = Number.isFinite(percent);
  const total = Number(sale.totalAmount) || 0;
  const amount =
    sale.commissionAmount != null && sale.commissionAmount !== ""
      ? Number(sale.commissionAmount)
      : hasPercent
        ? (total * percent) / 100
        : null;

  if (amount == null || !Number.isFinite(Number(amount))) return "-";

  const fmt = (v) =>
    `$${Number(v || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  return hasPercent ? `${fmt(amount)} (${percent}%)` : fmt(amount);
}
